import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaToggleOn, FaToggleOff, FaPlay, FaHistory } from 'react-icons/fa';

const API = process.env.REACT_APP_API_BASE_URL;

const CatalogExportCard = ({ title, icon, model, prefix, label, darkMode }) => {
  const [syncTime, setSyncTime] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [companyIds, setCompanyIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [runningNow, setRunningNow] = useState(false);
  const [logs, setLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [running, setRunning] = useState(false);
  const [lastRunAt, setLastRunAt] = useState(null);
  const [lastRunStatus, setLastRunStatus] = useState('never');
  const [mongoUrl, setMongoUrl] = useState(null);
  const [companies, setCompanies] = useState([]);

  const cardBg = darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white';
  const textMuted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const textLabel = darkMode ? 'text-gray-300' : 'text-gray-700';
  const text = darkMode ? 'text-gray-100' : 'text-gray-900';
  const inputCls = darkMode
    ? 'bg-gray-700 text-gray-100 border-gray-600 placeholder-gray-500 focus:ring-blue-400'
    : 'bg-white text-gray-900 border-gray-300 placeholder-gray-400 focus:ring-blue-500';
  const divider = darkMode ? 'border-gray-700' : 'border-gray-200';

  const applyConfig = useCallback((config) => {
    if (!config) return;
    setMongoUrl(config.mongoUrl);
    setSyncTime(config[`${prefix}SyncTime`] || '');
    setEnabled(config[`${prefix}Enabled`] === true);
    setRunning(config[`${prefix}Running`] === true);
    setCompanyIds((config[`${prefix}ExportCompanyIds`] || []).map(id => id.toString()));
    setLastRunAt(config[`${prefix}LastRunAt`] || null);
    setLastRunStatus(config[`${prefix}LastRunStatus`] || 'never');
  }, [prefix]);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/super-admin/sync-config`);
      applyConfig(res.data.config);
    } catch (_) {}
  }, [applyConfig]);

  const fetchCompanies = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/super-admin/companies`);
      setCompanies(res.data.companies || []);
    } catch (_) {}
  }, []);

  useEffect(() => { fetchConfig(); fetchCompanies(); }, [fetchConfig, fetchCompanies]);

  const loadLogs = async () => {
    setLoadingLogs(true);
    try {
      const res = await axios.get(`${API}/super-admin/sync-config/catalog-logs?model=${model}`);
      setLogs(res.data.logs || []);
      setSyncTime(res.data.syncTime || '');
      setEnabled(res.data.enabled === true);
      setRunning(res.data.running === true);
      setLastRunAt(res.data.lastRunAt || null);
      setLastRunStatus(res.data.lastRunStatus || 'never');
    } catch (_) {} finally { setLoadingLogs(false); }
  };

  const handleShowLogs = () => {
    if (!showLogs) loadLogs();
    setShowLogs(v => !v);
  };

  const waitForRun = () => {
    let attempts = 0;
    const timer = setInterval(async () => {
      attempts += 1;
      try {
        const res = await axios.get(`${API}/super-admin/sync-config`);
        applyConfig(res.data.config);
        if (showLogs) loadLogs();
        if (!res.data.config?.[`${prefix}Running`] || attempts >= 200) clearInterval(timer);
      } catch (_) {
        if (attempts >= 200) clearInterval(timer);
      }
    }, 3000);
  };

  const handleSaveTime = async () => {
    if (!syncTime) { toast.error('Please select a daily sync time'); return; }
    if (!mongoUrl) { toast.error('Please save the Auto Sync config (MongoDB URL) first'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/super-admin/sync-config`, { [`${prefix}SyncTime`]: syncTime });
      applyConfig(res.data.config);
      toast.success(`${label} sync time saved. Runs daily at ${syncTime}.`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save config');
    } finally { setSaving(false); }
  };

  const handleCompanyToggle = async (id) => {
    const next = companyIds.includes(id)
      ? companyIds.filter(c => c !== id)
      : [...companyIds, id];
    setCompanyIds(next);
    try {
      const res = await axios.post(`${API}/super-admin/sync-config`, { [`${prefix}ExportCompanyIds`]: next });
      applyConfig(res.data.config);
      toast.success(next.length > 0 ? `Export companies saved (${next.length})` : 'Export companies cleared');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save companies');
    }
  };

  const handleToggle = async () => {
    try {
      const res = await axios.patch(`${API}/super-admin/sync-config/catalog-toggle`, { model });
      setEnabled(res.data.enabled);
      toast.success(res.data.enabled ? `${label} daily sync enabled` : `${label} daily sync paused`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Toggle failed');
    }
  };

  const handleRunNow = async (full = false) => {
    setRunningNow(true);
    try {
      await axios.post(`${API}/super-admin/sync-config/catalog-run-now`, { model, full });
      toast.success((full ? 'Full' : 'Incremental') + ` ${label.toLowerCase()} export started — status updates while it runs`);
      waitForRun();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start export');
    } finally { setRunningNow(false); }
  };

  const statusBadge = () => {
    if (running) {
      return (
        <span className="flex items-center gap-1.5 text-xs font-medium text-amber-500">
          <FaPlay className="animate-pulse text-xs" /> Running…
        </span>
      );
    }
    if (lastRunStatus === 'never') return <span className={`text-xs ${textMuted}`}>Never run</span>;
    return (
      <span className={`text-xs font-medium ${lastRunStatus === 'success' ? 'text-green-500' : 'text-red-500'}`}>
        Last: {lastRunStatus === 'success' ? '✓' : '✗'} {lastRunAt ? new Date(lastRunAt).toLocaleString() : ''}
      </span>
    );
  };

  return (
    <div className={`rounded-lg shadow mb-6 overflow-hidden ${cardBg}`}>
      <div className={`px-5 py-3 border-b ${divider} flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <span className={`text-blue-500 ${enabled ? 'animate-pulse' : ''}`}>{icon}</span>
          <span className={`font-semibold text-sm ${textLabel}`}>{title}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
            !mongoUrl ? `${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}` :
            !syncTime ? `${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}` :
            enabled ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
          }`}>
            {!mongoUrl ? 'No target DB configured' : !syncTime ? 'No time set' : enabled ? `Active — daily at ${syncTime}` : 'Paused'}
          </span>
        </div>
        <div className="flex items-center gap-2">{statusBadge()}</div>
      </div>

      <div className="p-5 space-y-4">
        {!mongoUrl ? (
          <p className={`text-sm ${textMuted}`}>Save the Auto Sync config (MongoDB URL) above first — the export reuses the same target database.</p>
        ) : (
          <>
            {/* Company selection */}
            <div>
              <label className={`block text-sm font-semibold mb-1.5 ${textLabel}`}>Export Companies</label>
              <div className={`border rounded max-h-40 overflow-y-auto ${inputCls}`}>
                {companies.length === 0 && (
                  <p className={`px-3 py-2 text-sm ${textMuted}`}>No companies found</p>
                )}
                {companies.map(c => (
                  <label key={c._id} className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-blue-50 dark:hover:bg-gray-600">
                    <input
                      type="checkbox"
                      checked={companyIds.includes(c._id)}
                      onChange={() => handleCompanyToggle(c._id)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className={`text-sm ${text}`}>{c.name}</span>
                  </label>
                ))}
              </div>
              <p className={`text-xs mt-1.5 ${textMuted}`}>
                Only {label.toLowerCase()} of the selected companies are exported. Incremental runs export only
                records created or updated since the last run (by updatedAt). When a company is selected for both
                Tally Items and Tally Stock Groups, items and their parent stock groups are interlinked in the
                target database.
              </p>
            </div>

            {/* Daily time */}
            <div>
              <label className={`block text-sm font-semibold mb-1.5 ${textLabel}`}>Daily Sync Time</label>
              <div className="flex items-center gap-2">
                <input
                  type="time"
                  value={syncTime}
                  onChange={e => setSyncTime(e.target.value)}
                  className={`flex-1 border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 ${inputCls}`}
                />
                <button
                  onClick={handleSaveTime}
                  disabled={saving || !syncTime}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50 transition whitespace-nowrap"
                >
                  {saving ? 'Saving...' : 'Save Time'}
                </button>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2 pt-1">
              <button
                onClick={handleToggle}
                disabled={!syncTime || companyIds.length === 0}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded transition disabled:opacity-50 ${
                  enabled
                    ? 'bg-yellow-500 hover:bg-yellow-600 text-white'
                    : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
              >
                {enabled ? <><FaToggleOff /> Pause</> : <><FaToggleOn /> Enable Daily Sync</>}
              </button>
              <button
                onClick={() => handleRunNow(false)}
                disabled={runningNow || running || companyIds.length === 0}
                className="flex items-center gap-1.5 px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white text-sm font-medium rounded disabled:opacity-50 transition"
              >
                <FaPlay /> {running ? 'Running…' : 'Run Incremental'}
              </button>
              <button
                onClick={() => handleRunNow(true)}
                disabled={runningNow || running || companyIds.length === 0}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded transition disabled:opacity-50 ${
                  darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                <FaPlay /> {running ? 'Running…' : 'Run Full'}
              </button>
              <button
                onClick={handleShowLogs}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded transition ${
                  darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                <FaHistory /> {showLogs ? 'Hide Logs' : 'Logs'}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Logs */}
      {showLogs && (
        <div className={`border-t ${divider}`}>
          {loadingLogs ? (
            <p className={`px-5 py-4 text-sm ${textMuted}`}>Loading logs...</p>
          ) : logs.length === 0 ? (
            <p className={`px-5 py-4 text-sm ${textMuted}`}>No sync logs yet.</p>
          ) : (
            <div className="max-h-72 overflow-y-auto">
              {logs.map((log, i) => (
                <div key={i} className={`px-5 py-3 border-b ${divider} last:border-0`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-medium ${log.status === 'success' ? 'text-green-500' : 'text-red-500'}`}>
                        {log.status === 'success' ? '✓ Success' : '✗ Error'}
                      </span>
                      {log.message && <span className={`text-xs ${textMuted}`}>{log.message}</span>}
                    </div>
                    <span className={`text-xs ${textMuted}`}>{new Date(log.runAt).toLocaleString()}</span>
                  </div>
                  {log.results?.length > 0 && (
                    <div className="flex flex-wrap gap-3 mt-1">
                      {log.results.map((r, j) => (
                        <span key={j} className={`text-xs ${textMuted}`}>
                          {r.company}: {r.upserted}/{r.total}
                          {r.errors > 0 && <span className="text-red-400 ml-1">({r.errorMessage})</span>}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CatalogExportCard;